import React, { useState } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { projects } from '../../data/projectsData';
import './Projects.css';
import './projects-sections-responsive.css';

const Projects: React.FC = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });
  const navigate = useNavigate();

  // State for filtering
  const [filter, setFilter] = useState('all');

  // Filter projects based on current filter
  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(project => project.category.includes(filter));

  // Handle navigating to dynamic project page
  const handleProjectClick = (projectId: number) => {
    navigate(`/project/${projectId}`);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const projectVariants = {
    hidden: { y: 25, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        duration: 0.5
      }
    }
  };

  // Dynamic filter lists
  const filterCategories = [
    { key: 'all', label: 'All Projects' },
    { key: 'ai', label: 'AI & Agents' },
    { key: 'web', label: 'Web Apps' },
    { key: 'hackathon', label: 'Hackathons' },
    { key: 'cpp', label: 'C++ Systems' },
    { key: 'data-structures', label: 'Data Structures' },
    { key: 'algorithm', label: 'Algorithms' },
    { key: 'python', label: 'Python & Bot' },
    { key: 'assembly', label: 'Assembly' }
  ];

  return (
    <section id="projects" className="projects-section py-5">
      <div className="radial-glow-projects" />
      <Container className="py-4">
        <motion.div 
          className="section-title text-center mb-5"
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="title-container mb-2">
            <h2>My Projects Portfolio</h2>
          </div>
          <p className="subtitle text-secondary">A curated collection of full-stack platforms, low-level programs, and agentic systems.</p>
          <motion.p 
            className="featured-portfolio-link mt-3"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Visit legacy site: <a href="https://rebelhere.github.io/muhammadrahim/" target="_blank" rel="noopener noreferrer" className="legacy-link">Muhammad Rahim Portfolio</a>
          </motion.p>
        </motion.div>

        {/* Filter Toolbar */}
        <div className="filter-buttons text-center mb-5 d-flex flex-wrap justify-content-center gap-2">
          {filterCategories.map((category) => (
            <motion.div
              key={category.key}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
            >
              <Button 
                variant={filter === category.key ? 'primary' : 'outline-primary'} 
                className={`filter-btn px-4 py-2 ${filter === category.key ? 'active' : ''}`} 
                onClick={() => setFilter(category.key)}
              >
                {category.label}
              </Button>
            </motion.div>
          ))}
        </div>

        {/* Projects Grid with AnimatePresence */}
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "visible"}
          className="projects-grid-wrapper"
        >
          <Row className="projects-grid">
            <AnimatePresence mode="popLayout">
              {filteredProjects.map((project) => (
                <Col lg={4} md={6} className="mb-4 project-grid-col" key={project.id}>
                  <motion.div 
                    layout
                    variants={projectVariants}
                    className="project-card h-100"
                    whileHover={{ 
                      y: -8,
                      borderColor: 'var(--accent-color)',
                      boxShadow: "0 20px 40px rgba(99, 102, 241, 0.12)"
                    }}
                    onClick={() => handleProjectClick(project.id)}
                  >
                    <div className="project-image-wrapper position-relative overflow-hidden">
                      {project.hackathon && (
                        <div className="hackathon-card-badge position-absolute top-0 end-0 m-3 z-3">
                          <span className="badge bg-danger text-white d-flex align-items-center gap-1 px-3 py-1.5 rounded-pill shadow-lg border border-danger-subtle" style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.5px', background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)' }}>
                            ⚡ HACKATHON
                          </span>
                        </div>
                      )}
                      <img src={project.image} alt={project.title} className="project-image w-100 h-100 object-fit-cover" />
                      <motion.div 
                        className="project-overlay d-flex align-items-center justify-content-center"
                        initial={{ opacity: 0 }}
                        whileHover={{ opacity: 1 }}
                        transition={{ duration: 0.25 }}
                      >
                        <motion.span 
                          className="view-project-badge px-4 py-2.5 rounded-pill font-weight-bold"
                          whileHover={{ scale: 1.05 }}
                        >
                          Explore Details →
                        </motion.span>
                      </motion.div>
                    </div>
                    <div className="project-info p-4 d-flex flex-column justify-content-between">
                      <div className="project-meta-details mb-3">
                        <span className="project-category-tag-sub text-uppercase font-weight-bold mb-2 d-inline-block">
                          {project.category.slice(0, 2).join(' & ')}
                        </span>
                        <h4 className="project-card-title mb-2 font-display">{project.title}</h4>
                        <p className="project-card-description text-secondary mb-0">{project.description}</p>
                      </div>
                      <div className="project-tech-tags d-flex flex-wrap gap-2 mt-auto">
                        {project.technologies.slice(0, 3).map((tech, index) => (
                          <span key={index} className="tech-tag">{tech}</span>
                        ))}
                        {project.technologies.length > 3 && (
                          <span className="tech-tag font-weight-bold">+{project.technologies.length - 3}</span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                </Col>
              ))}
            </AnimatePresence>
          </Row>
        </motion.div>
      </Container>
    </section>
  );
};

export default Projects;
